import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * ScrollManager
 * ─────────────
 * Standard SPA scroll behaviour:
 *   1. On PUSH / REPLACE (link clicks) → scroll to top instantly.
 *   2. On POP  (back/forward)          → restore the previous scroll offset.
 *   3. On hash change (#services)      → smooth-scroll to the target section.
 *
 * We disable the browser's native scroll-restoration and manage it ourselves
 * because React Router mounts the new component asynchronously; the browser
 * would otherwise try to restore before the DOM is tall enough.
 */
export default function ScrollManager() {
  const { pathname, hash, key } = useLocation();
  const navType = useNavigationType(); // "PUSH" | "REPLACE" | "POP"
  const positions = useRef(new Map()); // key -> scrollY
  const lastKey = useRef(key);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Save scroll position on every scroll event, keyed by the current history entry.
  useEffect(() => {
    const onScroll = () => {
      positions.current.set(lastKey.current, window.scrollY);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    // Hash-based anchor: smooth-scroll to the section after mount.
    if (hash) {
      const id = hash.replace(/^#/, "");
      const attempts = [50, 200, 500, 900];
      attempts.forEach((delay) => {
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, delay);
      });
      lastKey.current = key;
      return;
    }

    if (navType === "POP") {
      // Restore previous scroll for this history key (default: top).
      const saved = positions.current.get(key) ?? 0;
      // Delay a tick so the new page has committed to the DOM.
      requestAnimationFrame(() => {
        window.scrollTo({ top: saved, left: 0, behavior: "auto" });
      });
    } else {
      // PUSH / REPLACE: fresh page → top.
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    lastKey.current = key;
  }, [pathname, hash, key, navType]);

  return null;
}
